import { clsx } from 'clsx'
import { type FC } from 'react'

import { Link } from 'base-ui'

import {
  CROSS_SELL_ANALYTICS_ATTR_IMAGE,
  CROSS_SELL_ANALYTICS_ATTR_TITLE,
} from '../../../../cross-sell'
import {
  CSW_INTERACTED_KEY,
  useSetSessionStorageValuesForOptimizelyTracking,
} from '../../../../experiments/hooks'
import { useExperimentalAddStaticFileHost } from '../../../../experiments/new-cdn-experiment/useExperimentalAddStaticFileHost'
import {
  IMAGE_PRODUCT_FALLBACK_SRC,
  NextImageWithFallback,
} from '../../../../image'
import { ProductRedPointsBadge } from '../../ProductRedPointsBadge'

type ProductCrossSellProductThumbnailProps = {
  className?: string
  imageAsUrl?: boolean
  imageUrl?: string
  onGtmDataLayerPush?: () => void
  productId: number | string
  productName: string
  productUrl: string
  redpoints?: number
}

export const ProductCrossSellProductThumbnail: FC<
  ProductCrossSellProductThumbnailProps
> = ({
  className,
  imageAsUrl,
  imageUrl,
  onGtmDataLayerPush,
  productId,
  productName,
  productUrl,
  redpoints,
}) => {
  const { addStaticCdnHostToUrl } = useExperimentalAddStaticFileHost()
  const trackClick =
    useSetSessionStorageValuesForOptimizelyTracking(CSW_INTERACTED_KEY)

  const fallback = addStaticCdnHostToUrl(IMAGE_PRODUCT_FALLBACK_SRC)
  const productImage = (
    <NextImageWithFallback
      alt={productName}
      className="h-auto max-w-full bg-light-primary-low"
      data-analytics={CROSS_SELL_ANALYTICS_ATTR_IMAGE}
      fallbackSrc={fallback}
      height={150}
      src={imageUrl ?? fallback}
      width={150}
    />
  )

  return (
    <div className={clsx('relative', className)} data-product-id={productId}>
      {imageAsUrl ? (
        <a
          className="mb-2 flex aspect-square items-center justify-center bg-light-primary-low p-3.5"
          data-analytics={CROSS_SELL_ANALYTICS_ATTR_TITLE}
          href={productUrl}
          onClick={() => {
            onGtmDataLayerPush?.()
            trackClick()
          }}
        >
          {productImage}
        </a>
      ) : (
        <div className="mb-2 flex aspect-square items-center justify-center bg-light-primary-low p-3.5">
          {productImage}
        </div>
      )}
      <h3 className="text-center">
        <Link
          className="line-clamp-3 overflow-hidden text-ellipsis text-center font-medium text-dark-primary-max no-underline"
          data-analytics={CROSS_SELL_ANALYTICS_ATTR_TITLE}
          href={productUrl}
          onClick={() => {
            onGtmDataLayerPush?.()
            trackClick()
          }}
        >
          {productName}
        </Link>
      </h3>
      {redpoints ? (
        <div className="absolute right-0 top-0">
          <ProductRedPointsBadge
            className="size-16 text-xs font-normal lowercase"
            redPointsAmount={redpoints}
          />
        </div>
      ) : null}
    </div>
  )
}
